import type { TextFont } from "common/constants/textFont";
import { TextFonts } from "common/constants/textFont";
import { ensureDefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { LRUStringCache } from "common/utils/lruStringCache";
import { getWindowPersistentValue, setWindowPersistentValue } from "common/utils/persistentValue";
import stableHash from "common/utils/stableHash";
import {
  PlacementXAnchor,
  PlacementYAnchor,
  TextAlignment,
  textAlignmentToNamedAlignment,
} from "common/utils/textAlignment";
import type { IUniform, Mesh, Object3D } from "three";
import { Matrix4, MeshBasicMaterial } from "three";
import type { TextRenderInfo } from "troika-three-text";
import { configureTextBuilder, Text } from "troika-three-text";
import { defaultStencilProperties } from "./defaultStencilProperties";
import type { HighlightDataTexture } from "./HighlightDataTexture";
import { includeEntityHighlight } from "./materials/customizeShader";
import { setMeshIdAttribute } from "./meshIdAttribute";

interface TextRenderScene {
  markRenderingNeedsUpdate(): void;
}

const SDF_GLYPH_SIZE = 256;

type GfxTextAnchorX = "center" | "left" | "right";
type GfxTextAnchorY = "top" | "bottom" | "middle";

// Disable worker threads for improved safety/stability.
const hasConfiguredTextBuilder = getWindowPersistentValue<boolean>("hasConfiguredTextBuilder", false);
if (!hasConfiguredTextBuilder) {
  configureTextBuilder({
    useWorker: false,
  });
  setWindowPersistentValue("hasConfiguredTextBuilder", true);
}

function placementYAnchorToGfxAnchorY(placementYAnchor: PlacementYAnchor): GfxTextAnchorY {
  switch (placementYAnchor) {
    case PlacementYAnchor.BOTTOM:
      return "bottom";
    case PlacementYAnchor.CENTER:
      return "middle";
    case PlacementYAnchor.TOP:
      return "top";
    default:
      assertUnreachable(placementYAnchor);
  }
}

function placementXAnchorToGfxAnchorX(placementXAnchor: PlacementXAnchor): GfxTextAnchorX {
  switch (placementXAnchor) {
    case PlacementXAnchor.LEFT:
      return "left";
    case PlacementXAnchor.CENTER:
      return "center";
    case PlacementXAnchor.RIGHT:
      return "right";
    default:
      assertUnreachable(placementXAnchor);
  }
}

export interface TextToTHREEOptions {
  id?: number;
  text: string;
  scale?: number;
  width?: number;
  alignment?: TextAlignment;
  anchorX?: PlacementXAnchor;
  anchorY?: PlacementYAnchor;
  lineHeight?: number;
  yUpPositive?: boolean;
  x?: number;
  y?: number;
  angle?: number;
  z?: number;
  verticalOffset?: number;
  transform?: Matrix4;
  layers?: number;
  font?: TextFont;
  fontSize?: number;
  color?: number | string;
  outlineColor?: number | string; // Defaults to same as color
  outlineWidth?: number; // Defaults to no outline
  renderOrder?: number;

  useHighlighting?: boolean;
}

export type TextToTHREEResult = [Promise<TextRenderInfo>, Mesh];

type CollectTextPromiseFunc = (promise: Promise<TextRenderInfo>) => void;

export class THREETextRenderer {
  private readonly tmpMatrix = new Matrix4();
  private readonly highlightedIdsSharedUniform: IUniform = { value: null };
  private readonly isolateModeSharedUniform: IUniform = { value: false };
  private readonly textBaseMaterial = new MeshBasicMaterial({
    ...defaultStencilProperties,
    name: "THREETextRenderer textBaseMaterial",
  });
  private readonly textRenderInfoCache = new LRUStringCache<TextRenderInfo>(5000);

  private readonly textBaseMaterialNoHighlighting = new MeshBasicMaterial({
    ...defaultStencilProperties,
    name: "THREETextRenderer textBaseMaterialNoHighlighting",
  });

  // This callback is used exclusively for testing.
  private collectTextPromiseCallback: CollectTextPromiseFunc | null = null;

  private static _instance: THREETextRenderer | undefined;

  static get instance(): THREETextRenderer {
    return this._instance ?? (this._instance = new THREETextRenderer());
  }

  constructor() {
    this.textBaseMaterial.defines = { ISOLATE_MODE: 1 };

    this.textBaseMaterial.onBeforeCompile = (shader) => {
      includeEntityHighlight(shader, true);
      shader.uniforms.highlightedIds = this.highlightedIdsSharedUniform;
      shader.uniforms.isolateMode = this.isolateModeSharedUniform;
    };

    this.textBaseMaterialNoHighlighting.onBeforeCompile = (shader) => {
      includeEntityHighlight(shader, true);
      shader.uniforms.highlightedIds = { value: null };
      shader.uniforms.isolateMode = { value: false };
    };
  }

  prepareRender(highlightDataTexture?: HighlightDataTexture): void {
    this.highlightedIdsSharedUniform.value = highlightDataTexture?.texture ?? null;
    this.isolateModeSharedUniform.value = highlightDataTexture?.isolateMode ?? null;
  }

  /**
   * Generates a Text Mesh with the given options. Note that the returned value is memoized and so
   * should not be mutated. Without memoization, we pay a signfificant performance penalty.
   *
   * @returns A memoized result value. This value should not be mutated!
   */
  renderText(scene: TextRenderScene, options: TextToTHREEOptions): TextToTHREEResult {
    const {
      text,
      scale = 1,
      alignment = TextAlignment.LEFT,
      anchorX = PlacementXAnchor.CENTER,
      anchorY = PlacementYAnchor.BOTTOM,
      id,
      lineHeight,
      x = 0,
      y = 0,
      z = 0,
      layers,
      angle = 0,
      yUpPositive = true,
      verticalOffset = 0,
      transform,
      font = TextFonts.ArialNarrow,
      fontSize = 42,
      color = 0,
      outlineColor = color,
      outlineWidth = 0,
      renderOrder = 0,
      useHighlighting = true,
    } = options;

    let width = options.width;
    if (width !== undefined) {
      width /= scale;
    }

    const textMesh = new Text();

    // troika measures the stroke width as a "radius", while pdfkit uses a "diameter".
    const troikaOutlineWidth = outlineWidth / 2;

    textMesh.material = useHighlighting ? this.textBaseMaterial : this.textBaseMaterialNoHighlighting;
    // force default material for outlines to avoid highlighting them.
    textMesh.forceDefaultMaterialForOutlines = true;

    textMesh.text = text.replace(/\t/g, "    ");
    textMesh.position.x = x;
    textMesh.position.y = y;
    textMesh.position.z = z;
    textMesh.textAlign = textAlignmentToNamedAlignment(alignment);
    textMesh.lineHeight = lineHeight ?? "normal";
    textMesh.maxWidth = width;
    textMesh.font = font.path;
    textMesh.fontSize = fontSize;
    textMesh.color = color;
    textMesh.outlineWidth = troikaOutlineWidth;
    textMesh.outlineColor = outlineColor;
    textMesh.anchorX = placementXAnchorToGfxAnchorX(anchorX);
    textMesh.anchorY = placementYAnchorToGfxAnchorY(anchorY);
    textMesh.sdfGlyphSize = SDF_GLYPH_SIZE;
    textMesh.renderOrder = renderOrder;

    if (layers) {
      textMesh.layers.set(layers);
    }

    textMesh.matrixAutoUpdate = false;
    textMesh.matrix.identity();
    textMesh.matrix.multiply(this.tmpMatrix.makeScale(scale, yUpPositive ? scale : -scale, scale));
    textMesh.matrix.multiply(this.tmpMatrix.makeTranslation(0, verticalOffset, 0));
    textMesh.matrix.premultiply(this.tmpMatrix.makeRotationZ(angle));
    textMesh.matrix.premultiply(this.tmpMatrix.makeTranslation(x, y, z));
    if (transform) {
      textMesh.matrix.premultiply(transform);
    }
    textMesh.updateMatrixWorld(true);

    if (id !== undefined) {
      setMeshIdAttribute(textMesh, id);
    }

    const textRenderInfoCacheKey = stableHash({
      // TextRenderInfo generation only depends on a subset of the options properties
      // For the full list, see: https://github.com/protectwise/troika/blob/main/packages/troika-three-text/src/Text.js#L422
      text: options.text,
      font: options.font,
      fontSize: options.fontSize,
      lineHeight: options.lineHeight,
      alignment: options.alignment,
      width: options.width,
      anchorX: options.anchorX,
      anchorY: options.anchorY,
    });

    const textRenderInfoPromise = new Promise<TextRenderInfo>((resolve) => {
      if (this.textRenderInfoCache.has(textRenderInfoCacheKey)) {
        applyDefaultStencilPropertiesToMesh(textMesh);

        const textRenderInfo = ensureDefined(this.textRenderInfoCache.get(textRenderInfoCacheKey));

        // Replicates the code behavior in:
        // https://github.com/protectwise/troika/blob/main/packages/troika-three-text/src/Text.js#L411

        textMesh._textRenderInfo = textRenderInfo;
        textMesh._needsSync = false;
        textMesh._isSyncing = false;

        textMesh.geometry.updateGlyphs(
          textRenderInfo.glyphBounds,
          textRenderInfo.glyphAtlasIndices,
          textRenderInfo.blockBounds,
          textRenderInfo.chunkedBounds,
          textRenderInfo.glyphColors,
        );

        resolve(textRenderInfo);
        scene.markRenderingNeedsUpdate();

        return;
      }

      textMesh.sync(() => {
        applyDefaultStencilPropertiesToMesh(textMesh);

        this.textRenderInfoCache.add(textRenderInfoCacheKey, textMesh._textRenderInfo);
        resolve(textMesh.textRenderInfo);
        scene.markRenderingNeedsUpdate();
      });
    });

    if (this.collectTextPromiseCallback) {
      this.collectTextPromiseCallback(textRenderInfoPromise);
    }

    return [textRenderInfoPromise, textMesh];
  }

  addText(scene: TextRenderScene, options: TextToTHREEOptions, dest: Object3D): void {
    // Skip empty strings to work around lack of handling in troika-three-text.
    if (options.text.trim() !== "") {
      const [, textMesh] = this.renderText(scene, options);
      dest.add(textMesh);
    }
  }

  setCollectTextPromiseCallback(callback: CollectTextPromiseFunc): void {
    this.collectTextPromiseCallback = callback;
  }
}

/**
 * When Troika constructs the outline material, it doesn't copy the stencil properties from the base material.
 * This is due to our own patch to the troika package that is surfaced with the `forceDefaultMaterialForOutlines`
 * property. Without that set, this change is unnecessary.
 */
function applyDefaultStencilPropertiesToMesh(mesh: Mesh) {
  if (Array.isArray(mesh.material)) {
    for (const mtl of mesh.material) {
      for (const id in defaultStencilProperties) {
        mtl[id] = defaultStencilProperties[id];
      }
    }
  } else {
    for (const id in defaultStencilProperties) {
      mesh.material[id] = defaultStencilProperties[id];
    }
  }
}
